import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { LanguageProvider } from './contexts/LanguageContext'
import { WebSocketProvider } from './contexts/WebSocketContext'
import { BrokerContextProvider } from './hooks/useBrokerContext'
import { useAuth } from './hooks/useAuth'

// MortgageFlow.pro - Modern React frontend with GitHub auto-deployment
// 🎉 SUCCESS TEST - Vercel webhook fixed, both platforms ready!
// SavedScenarios functionality now integrated into MortgageCalculator
import Dashboard from './pages/Dashboard'
import Login from './pages/Login'
import Landing from './pages/Landing'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import Contacts from './pages/Contacts'
import CustomerProfilePage from './pages/CustomerProfilePage'
import Tasks from './pages/Tasks'
import Leads from './pages/Leads'
import DealsBoard from './pages/DealsBoard'
import ApiKeysSettings from './pages/ApiKeysSettings'
import DealDetail from './pages/DealDetail'
import Conversations from './pages/Conversations'
import Calls from './pages/Calls'
import Settings from './pages/Settings'
import SettingsLayout from './pages/SettingsLayout'
import UserProfile from './pages/UserProfile'
import SmsSettings from './pages/SmsSettings'
import PhoneNumberSettings from './pages/PhoneNumberSettings'
import AdminPhoneNumberManagement from './pages/AdminPhoneNumberManagement'
import TelnyxSettings from './pages/TelnyxSettings'
// DealStatusSettings removed — board statuses are now managed inline via BoardStatusPanel
import FinancialSettings from './pages/FinancialSettings'
import DealTypesSettings from './pages/DealTypesSettings'
import ScenarioBuilder from './pages/ScenarioBuilder'
import MortgageCalculator from './pages/MortgageCalculator'
import Users from './pages/Users'
import AdminUserDetail from './pages/AdminUserDetail'
import AdminDeals from './pages/AdminDeals'
import AdminTasks from './pages/AdminTasks'
import AdminStripeSettings from './pages/AdminStripeSettings'
import AdminMembershipSettings from './pages/AdminMembershipSettings'
import AdminBillingManagement from './pages/AdminBillingManagement'
import BrokerMembershipSettings from './pages/BrokerMembershipSettings'
import CustomerNameMigration from './pages/CustomerNameMigration'
import AdminAnnouncements from './pages/AdminAnnouncements'
import AnnouncementsFeed from './pages/AnnouncementsFeed'
import LeadsSettings from './pages/LeadsSettings'
import CustomForm from './pages/CustomForm'
import ColumnSettings from './pages/ColumnSettings'
import UsersManagement from './pages/UsersManagement'
import Reports from './pages/reports'
import CollaboratorRegistration from './pages/CollaboratorRegistration'
import WorkflowManagement from './pages/WorkflowManagement'
import AdvancedTeamManagement from './pages/AdvancedTeamManagement'
import ScheduleSettings from './pages/ScheduleSettings'
import CustomFormSettings from './pages/CustomFormSettings'
import Calendar from './pages/Calendar'
import CalendarSettings from './pages/CalendarSettings'
import EmailSettings from './pages/EmailSettings'
import SearchResults from './pages/SearchResults'
import LiveChat from './pages/LiveChat'
import ChatSettings from './pages/ChatSettings'
import BrokerSupport from './pages/BrokerSupport'
import EnterpriseContact from './pages/EnterpriseContact'
import Contact from './pages/Contact'
import Layout from './components/Layout'
import LoadingSpinner from './components/LoadingSpinner'
import SecurityVerificationModal from './components/SecurityVerificationModal'
import OnboardingModal from './components/OnboardingModal'
import CookieConsent from './components/CookieConsent'
import { API_BASE_URL } from './utils/api'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, securityStatus, checkSecurityStatus, isBroker } = useAuth()
  const [showOnboardingModal, setShowOnboardingModal] = React.useState(false)
  const [trialStatus, setTrialStatus] = React.useState<any>(null)
  
  // Check security status when user is loaded
  React.useEffect(() => {
    if (user && !securityStatus) {
      checkSecurityStatus()
    }
  }, [user, securityStatus, checkSecurityStatus])
  
  // Check membership status for brokers to show onboarding modal
  React.useEffect(() => {
    const checkMembershipStatus = async () => {
      if (user && isBroker && securityStatus) {
        try {
          const token = localStorage.getItem('token')
          const response = await fetch(`${API_BASE_URL}/api/membership/manage/current`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
          if (response.ok) {
            const data = await response.json()
            const m = data.membership
            // Derive a trial_status shape compatible with downstream code
            const derivedTrialStatus = {
              is_eligible: !m.trial_used,
              is_active: m.is_trial_active,
              trial_used: m.trial_used,
              trial_started_at: m.trial_started_at,
              trial_ends_at: m.trial_ends_at,
              days_remaining: m.trial_days_remaining,
              has_seen_welcome_modal: m.has_seen_welcome_modal,
              has_active_subscription: ['active', 'trialing'].includes(m.subscription_status)
            }
            setTrialStatus(derivedTrialStatus)

            // Show onboarding modal for new users who haven't seen it yet
            if (!m.has_seen_welcome_modal && !m.trial_used) {
              setShowOnboardingModal(true)
            }
          }
        } catch (error) {
          console.error('Error checking membership status:', error)
        }
      }
    }

    checkMembershipStatus()
  }, [user, isBroker, securityStatus])
  
  if (loading) {
    return <LoadingSpinner />
  }
  
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Show onboarding modal for new users (needs security + trial)
  if (showOnboardingModal) {
    return (
      <>
        <OnboardingModal
          onComplete={async () => {
            setShowOnboardingModal(false)
            // Wait for security status to update before reloading
            await checkSecurityStatus()
            // Add a small delay to ensure backend has updated
            await new Promise(resolve => setTimeout(resolve, 1000))
            // Reload to apply new tier permissions
            window.location.reload()
          }}
        />
        {/* Render the page in background but user can't interact */}
        <div style={{ filter: 'blur(3px)', pointerEvents: 'none' }}>
          {children}
        </div>
      </>
    )
  }

  // Show security setup modal if user needs security features (but not if onboarding modal is showing)
  if (securityStatus?.needsSecuritySetup && !showOnboardingModal) {
    return (
      <>
        <SecurityVerificationModal
          isOpen={true}
          onVerified={() => {
            checkSecurityStatus()
          }}
          onSkip={async () => {
            // Wait a moment for backend to update
            await new Promise(resolve => setTimeout(resolve, 500))
            await checkSecurityStatus()
          }}
          userEmail={user.email}
          // No sessionToken - user is already authenticated
        />
        {/* Render the page in background but user can't interact */}
        <div style={{ filter: 'blur(3px)', pointerEvents: 'none' }}>
          {children}
        </div>
      </>
    )
  }
  
  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  
  if (loading) {
    return <LoadingSpinner />
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

// Admin-only route
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdmin } = useAuth()
  
  if (loading) {
    return <LoadingSpinner />
  }
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

// Broker-only route
function BrokerRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isBroker } = useAuth()
  
  if (loading) {
    return <LoadingSpinner />
  }
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  if (!isBroker) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

// Collaborator-only route
function CollaboratorRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isCollaborator } = useAuth()
  
  if (loading) {
    return <LoadingSpinner />
  }
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  if (!isCollaborator) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

// Broker or Collaborator route
function BrokerCollaboratorRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isBroker, isCollaborator } = useAuth()
  
  if (loading) {
    return <LoadingSpinner />
  }
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  if (!isBroker && !isCollaborator) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

// Placeholder components for missing settings pages
const TaskDelaysSettings = () => (
  <div className="p-6">
    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Task Delays</h1>
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
      <p className="text-gray-600 dark:text-gray-400">Task delay settings coming soon...</p>
    </div>
  </div>
)

const SystemSettings = () => (
  <div className="p-6">
    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">System Configuration</h1>
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
      <p className="text-gray-600 dark:text-gray-400">System configuration settings coming soon...</p>
    </div>
  </div>
)

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <WebSocketProvider>
            <BrokerContextProvider>
              <LanguageProvider>
                <Routes>
              {/* Public routes */}
              <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/enterprise-contact" element={<EnterpriseContact />} />
              <Route path="/register/collaborator/:token" element={<CollaboratorRegistration />} />
              <Route path="/broker/:slug" element={<CustomForm />} />
              
              {/* Protected routes */}
              <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              
              {/* Admin-only routes */}
              <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
              <Route path="users/:id" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
              <Route path="admin/deals" element={<AdminRoute><AdminDeals /></AdminRoute>} />
              <Route path="admin/tasks" element={<AdminRoute><AdminTasks /></AdminRoute>} />
              
              {/* Broker-only routes */}
              <Route path="leads" element={<BrokerRoute><Leads /></BrokerRoute>} />
              
              {/* Broker and Collaborator routes (based on permissions) */}
              <Route path="customers" element={<BrokerCollaboratorRoute><Contacts /></BrokerCollaboratorRoute>} />
              <Route path="customers/:id" element={<BrokerCollaboratorRoute><CustomerProfilePage /></BrokerCollaboratorRoute>} />
              <Route path="tasks" element={<BrokerCollaboratorRoute><Tasks /></BrokerCollaboratorRoute>} />
              <Route path="calendar" element={<BrokerCollaboratorRoute><Calendar /></BrokerCollaboratorRoute>} />
              {/* /pipeline is the canonical route; /deals redirects for backward compat */}
              <Route path="pipeline" element={<BrokerCollaboratorRoute><DealsBoard /></BrokerCollaboratorRoute>} />
              <Route path="deals" element={<Navigate to="/dashboard/pipeline" replace />} />
              <Route path="deals/:id" element={<BrokerCollaboratorRoute><DealDetail /></BrokerCollaboratorRoute>} />
              <Route path="pipeline/:id" element={<BrokerCollaboratorRoute><DealDetail /></BrokerCollaboratorRoute>} />
              <Route path="conversations" element={<BrokerCollaboratorRoute><Conversations /></BrokerCollaboratorRoute>} />
              <Route path="calls" element={<BrokerCollaboratorRoute><Calls /></BrokerCollaboratorRoute>} />
              <Route path="calculator" element={<BrokerCollaboratorRoute><MortgageCalculator /></BrokerCollaboratorRoute>} />
              <Route path="search" element={<BrokerCollaboratorRoute><SearchResults /></BrokerCollaboratorRoute>} />
              <Route path="support" element={<BrokerRoute><BrokerSupport /></BrokerRoute>} />
              <Route path="announcements" element={<BrokerCollaboratorRoute><AnnouncementsFeed /></BrokerCollaboratorRoute>} />
              <Route path="reports" element={<BrokerCollaboratorRoute><Reports /></BrokerCollaboratorRoute>} />
              
              {/* Settings routes - nested under SettingsLayout for Shopify-style sidebar */}
              <Route path="settings" element={<SettingsLayout />}>
                <Route index element={<></>} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="sms" element={<BrokerRoute><SmsSettings /></BrokerRoute>} />
                <Route path="phone-numbers" element={<BrokerRoute><PhoneNumberSettings /></BrokerRoute>} />
                <Route path="admin/telnyx" element={<AdminRoute><TelnyxSettings /></AdminRoute>} />
                <Route path="admin/phone-numbers" element={<AdminRoute><AdminPhoneNumberManagement /></AdminRoute>} />
                {/* deal-statuses page removed — edit statuses inline via the ⚙ icon on /pipeline */}
                <Route path="deal-statuses" element={<Navigate to="/dashboard/pipeline" replace />} />
                <Route path="financial" element={<BrokerRoute><FinancialSettings /></BrokerRoute>} />
                <Route path="deal-types" element={<BrokerRoute><DealTypesSettings /></BrokerRoute>} />
                <Route path="task-delays" element={<BrokerRoute><TaskDelaysSettings /></BrokerRoute>} />
                <Route path="system" element={<AdminRoute><SystemSettings /></AdminRoute>} />
                <Route path="stripe" element={<AdminRoute><AdminStripeSettings /></AdminRoute>} />
                <Route path="memberships" element={<AdminRoute><AdminMembershipSettings /></AdminRoute>} />
                <Route path="billing" element={<AdminRoute><AdminBillingManagement /></AdminRoute>} />
                <Route path="customer-migration" element={<AdminRoute><CustomerNameMigration /></AdminRoute>} />
                <Route path="announcements" element={<AdminRoute><AdminAnnouncements /></AdminRoute>} />
                <Route path="membership" element={<BrokerRoute><BrokerMembershipSettings /></BrokerRoute>} />
                <Route path="leads" element={<BrokerRoute><LeadsSettings /></BrokerRoute>} />
                <Route path="columns" element={<BrokerRoute><ColumnSettings /></BrokerRoute>} />
                <Route path="users-management" element={<BrokerRoute><UsersManagement /></BrokerRoute>} />
                <Route path="workflows" element={<BrokerRoute><WorkflowManagement /></BrokerRoute>} />
                <Route path="schedule" element={<BrokerRoute><ScheduleSettings /></BrokerRoute>} />
                <Route path="custom-forms" element={<BrokerRoute><CustomFormSettings /></BrokerRoute>} />
                <Route path="calendar" element={<BrokerRoute><CalendarSettings /></BrokerRoute>} />
                <Route path="email" element={<BrokerRoute><EmailSettings /></BrokerRoute>} />
                <Route path="api-keys" element={<BrokerRoute><ApiKeysSettings /></BrokerRoute>} />
                <Route path="live-chat" element={<BrokerRoute><LiveChat /></BrokerRoute>} />
                <Route path="chat" element={<BrokerRoute><ChatSettings /></BrokerRoute>} />
              </Route>
              <Route path="deals/:dealId/scenarios" element={<BrokerRoute><ScenarioBuilder /></BrokerRoute>} />
            </Route>
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
            </LanguageProvider>
          </BrokerContextProvider>
        </WebSocketProvider>
      </AuthProvider>
    </ThemeProvider>
    <CookieConsent />
    </HelmetProvider>
  )
}

export default App 